import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["buttonType", "fluid", "loading", "loadingVoiceOver", "margin", "marginBottom", "marginLeft", "marginRight", "marginTop", "marginX", "marginY", "size"];
import PropTypes from 'prop-types';
import React, { forwardRef, useContext } from 'react';
import { BUTTON_SIZES, BUTTON_TYPES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { Button } from '../Button/Button';
import { ButtonGroupContext } from '../ButtonGroup/ButtonGroup.context';
import { StyledLoadingButton } from './LoadingButton.styles';

/**
 * A `Button` component with loading state provided by `LoadingMask`.
 *
 * Make sure to add appropriate `loadingVoiceOver` text for assitive technologies.
 *
 * @related ['Button']
 * @extends LoadingMask
 * @since 8.1.0
 */
export var LoadingButton = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var buttonType = _ref.buttonType,
    fluid = _ref.fluid,
    loading = _ref.loading,
    loadingVoiceOver = _ref.loadingVoiceOver,
    margin = _ref.margin,
    marginBottom = _ref.marginBottom,
    marginLeft = _ref.marginLeft,
    marginRight = _ref.marginRight,
    marginTop = _ref.marginTop,
    marginX = _ref.marginX,
    marginY = _ref.marginY,
    size = _ref.size,
    rest = _objectWithoutProperties(_ref, _excluded);
  var context = useContext(ButtonGroupContext);
  var buttonProps = Object.assign({
    buttonType: context.buttonType || buttonType,
    fluid: context.fluid || fluid,
    size: context.size || size,
    ref: forwardedRef
  }, rest);
  var marginProps = {
    margin: margin,
    marginBottom: marginBottom,
    marginLeft: marginLeft,
    marginRight: marginRight,
    marginTop: marginTop,
    marginX: marginX,
    marginY: marginY
  };
  return /*#__PURE__*/React.createElement(StyledLoadingButton, _extends({
    fluid: context.fluid || fluid,
    loading: loading,
    loadingVoiceOver: loadingVoiceOver,
    iconSize: "sm"
  }, marginProps), /*#__PURE__*/React.createElement(Button, _extends({
    disabled: loading
  }, buttonProps)));
});
LoadingButton.displayName = 'LoadingButton';
LoadingButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The loading state.
   */
  loading: PropTypes.bool,
  /**
   * The `LoadingMask` `loadingVoiceOver` prop.
   */
  loadingVoiceOver: PropTypes.string,
  /**
   * A fluid button will take up all horizontal space.
   * Can be inherited from a parent ButtonGroup.
   *
   * Supports inline media query objects
   */
  fluid: CustomPropTypes.inlineMediaQuery(PropTypes.bool),
  /**
   * The size of the button.
   * Can be inherited from a parent ButtonGroup.
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /**
   * The type of the button.
   * Can be inherited from a parent ButtonGroup.
   */
  buttonType: PropTypes.oneOf(Object.values(BUTTON_TYPES))
} : {};
LoadingButton.defaultProps = {
  loading: false,
  loadingVoiceOver: 'Loading'
};